import {init as initPrism} from '../../../../assets/js/vendor/prism/main';
import {spinnerEl} from './elements';

const getLangFromFile = (name, ext) => {
    // Handle extension-less files and patterns first
    if (/^(M|m)akefile$/.test(name)) {
        return 'makefile';
    }
    if (name.startsWith('.env') || name === '.editorconfig') {
        return 'ini';
    }
    if (name.endsWith('rc')) {
        return 'json';
    }

    // Handle by file extension
    switch (ext) {
        // Core languages
        case 'c':
        case 'cpp':
        case 'cc':
        case 'h':
        case 'hpp':
            return 'clike';
        case 'css':
        case 'pcss':
            return 'css';
        case 'htm':
        case 'html':
        case 'xhtml':
        case 'xml':
        case 'svg':
        case 'mml':
        case 'mathml':
            return 'markup';

        // JavaScript family
        case 'jsx':
            return 'jsx';
        case 'tsx':
            return 'tsx';
        case 'ts':
        case 'cts':
        case 'mts':
            return 'typescript';
        case 'js':
        case 'cjs':
        case 'mjs':
            return 'javascript';

        // JSON family
        case 'json5':
            return 'json5';
        case 'json':
        case 'jsonc':
        case 'geojson':
            return 'json';

        // Other languages with multiple extensions
        case 'bash':
        case 'sh':
        case 'zsh':
            return 'bash';
        case 'gql':
        case 'graphql':
            return 'graphql';
        case 'ini':
        case 'conf':
        case 'config':
            return 'ini';
        case 'md':
        case 'markdown':
        case 'mdown':
            return 'markdown';
        case 'sql':
        case 'pgsql':
        case 'psql':
            return 'sql';
        case 'yml':
        case 'yaml':
            return 'yaml';

        // Single extensions
        case 'scss':
            return 'scss';

        // Fallback
        default:
            return 'plaintext';
    }
};

const handleFetchLoading = (isActive, treeViewItemEl, fileContentEl) => {
    const treeViewEl = treeViewItemEl.closest('[data-kui-tree-view]');
    const previewEl = fileContentEl.closest('[data-kui-code-file-preview]');
    if (!treeViewEl && !previewEl) {
        console.warn('No tree view or preview element found');
        return;
    }

    treeViewEl.setAttribute('aria-busy', isActive ? 'true' : 'false');
    previewEl.setAttribute('aria-busy', isActive ? 'true' : 'false');

    // Handle spinner
    if (isActive) {
        const spinner = spinnerEl();
        previewEl.appendChild(spinner);
    } else {
        const existingSpinnerEl = previewEl.querySelector('[data-kui-spinner]');
        existingSpinnerEl?.remove();
    }
};

const fetchAndRenderFile = async (fileRelPath, treeViewItemEl = null) => {
    if (!fileRelPath) {
        console.warn('No file relative path provided');
        return;
    }

    const fileContentEl = document.querySelector(
        '[data-kui-code-file-content]'
    );

    if (!fileContentEl) {
        console.error('File content element not found');
        return;
    }

    // Handle loading state if click-initiated
    if (treeViewItemEl) {
        handleFetchLoading(true, treeViewItemEl, fileContentEl);
    }

    try {
        const csrfToken = document.head.querySelector(
            '[name=csrf-token][content]'
        )?.content;

        const response = await fetch(
            `/make/v1/file_contents/${encodeURIComponent(fileRelPath)}`,
            {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    ...(csrfToken ? {'X-CSRF-Token': csrfToken} : {})
                }
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (!data || typeof data.name === 'undefined') {
            throw new Error('Invalid response data');
        }

        const fileName = data.name ?? 'unknown_file';
        const fileExt = fileName.split('.').pop()?.toLowerCase() ?? '';

        // Update content and language
        fileContentEl.textContent = data.content ?? '';
        const lang = getLangFromFile(fileName, fileExt);
        // Remove existing language classes first
        fileContentEl.classList.forEach((cls) => {
            if (cls.startsWith('language-')) {
                fileContentEl.classList.remove(cls);
            }
        });
        // Add new language class and ensure line-numbers exists
        fileContentEl.classList.add(`language-${lang}`, 'line-numbers');

        // Only initialise Prism for click events and non-plaintext languages
        if (treeViewItemEl && lang !== 'plaintext') {
            await initPrism(fileContentEl);
        }

        // Update breadcrumb
        const breadcrumbEl = document.querySelector(
            '[data-kui-code-file-path]'
        );

        if (breadcrumbEl) {
            let breadcrumbHTML = '';

            if (data.directory_path && data.directory_path.trim() !== '') {
                const pathParts = data.directory_path
                    .split(',')
                    .filter((part) => part.trim() !== '');

                // Add directory parts (all except the last one)
                for (let i = 0; i < pathParts.length; i++) {
                    const part = pathParts[i].trim();
                    breadcrumbHTML += `
                        <span>
                            ${part}
                            <span class="kui-util-hide-visually">/</span>
                        </span>
                        <svg class="kinde-icon kinde-icon-size-small" data-kinde-icon="true" fill="none" aria-hidden="true" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m9 18 6-6-6-6"/>
                        </svg>
                    `;
                }
            }

            // Always add the filename with file icon at the end
            breadcrumbHTML += `
                <span>
                    <svg class="kinde-icon kinde-icon-size-small" data-kinde-icon="true" fill="none" aria-hidden="true" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke="currentColor" stroke-width="1.5" d="M4.5 3.75a1 1 0 0 1 1-1h8.06a1 1 0 0 1 .737.324l4.94 5.39a1 1 0 0 1 .263.675V20.25a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1z"></path>
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M14 2.75v6h5.5"></path>
                        <path stroke="currentColor" stroke-linecap="round" stroke-width="1.5" d="M7.75 12.5h8.5m-8.5 3.75h8.5m-8.5-7.5h2.5"></path>
                    </svg>
                    ${fileName}
                </span>
            `;
            breadcrumbEl.innerHTML = breadcrumbHTML;
        }
    } catch (error) {
        console.error('Error fetching file contents:', error);
        fileContentEl.textContent = 'Error loading file content';
    } finally {
        // Only remove loading state if it was a click event
        if (treeViewItemEl) {
            handleFetchLoading(false, treeViewItemEl, fileContentEl);
        }
    }
};

const handleTreeViewItemClick = async (el) => {
    const fileRelPath = el.dataset.kuiTreeViewFileRelPath;
    await fetchAndRenderFile(fileRelPath, el);
};

const loadInitialFileContent = async (el) => {
    const fileRelPath = el.dataset.kuiCodeFileRelPath;
    await fetchAndRenderFile(fileRelPath);
};

export {handleTreeViewItemClick, loadInitialFileContent};
